import {
  createContext,
  useCallback,
  useContext,
  useState,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import PropTypes from 'prop-types';
import { useRouter } from 'next/router';

import { useUserContext } from '@context/UserContent';
import URL from '@helpers/url-constant';
import { formatDateToYYYYMMDD, loadState, removeState, saveState } from '@helpers/common';
import {
  getCampaign,
  launchDC,
  postCampaign,
  updateCampaign,
  saveDesignCampaignData,
  saveCampaignSetupData,
  saveCampaignReviewData,
  saveCampaignBasicInfo,
  saveOfferConfig,
  saveTargetUserConfig,
  updateCampaignBasicInfo,
} from '@apis/Campaign';
import CONSTANTS from '@helpers/constant';
import { useSettingContext } from './SettingContent';

const CampaignContext = createContext();

export function CampaignWrapper({ children }) {
  const router = useRouter();

  const { user } = useUserContext();
  const {
    setting: { countryId, deviceId },
  } = useSettingContext();

  const [campaignData, handleCampaignData] = useState({});
  const [preview, setPreview] = useState(false);
  const prevRoute = useRef(null);

  // Keep a ref to latest values so DC API callbacks stay stable
  const latestRef = useRef({ campaignData, accessToken: user.accessToken, countryId, deviceId });
  latestRef.current = { campaignData, accessToken: user.accessToken, countryId, deviceId };

  // get and save campaign when the application loads with the campaign pages
  useEffect(() => {
    async function fetchCampaignData() {
      const result = await getCampaign(user.accessToken, router.query.id || loadState('campId'));
      if (result.status && result.data.campaignId) {
        handleCampaignData(result.data);
      } else {
        resetCampaignData();
      }
    }

    // Wait until Next.js has parsed the URL query so `router.query.id` is
    // available — without this gate the effect can run before the id arrives
    // and route the user to select-campaign-type on a hard refresh.
    if (!router.isReady) return;

    // `/campaign/dc/create`, `/campaign/nom/create`, `/campaign/win/create`
    // are creation URLs *only* when there is no `?id=` in the query. With
    // an id present, the same URLs are reused for editing (see
    // useCampaignActions.handleEditRoute), so we must still fetch the
    // existing campaign.
    const isCampaignCreationEntry =
      !router.query.id &&
      (router.pathname === URL.CREATE_DC_CAMPAIGN ||
        router.pathname === URL.CREATE_NOM_CAMPAIGN ||
        router.pathname === URL.CREATE_WIN_CAMPAIGN);

    // Clear stale campaignData when starting a fresh creation flow.
    // Without this, the previous campaign's id/name/dates leak into
    // the new campaign's form and API payloads.
    if (isCampaignCreationEntry) {
      if (campaignData.id) {
        handleCampaignData({});
        removeState('campId');
      }
      return;
    }

    if (
      user.accessToken &&
      router.pathname.includes('campaign/') &&
      !router.pathname.includes('select-campaign-type')
    ) {
      prevRoute.current = true;
      if (router.query.id || loadState('campId')) {
        // Always refetch when entering a campaign route with a
        // different id — navigating from campaign A to campaign B
        // (edit / clone) must refresh the context. The prevRoute
        // guard that previously blocked this has been removed;
        // `router.query.id` is now in the dep array.
        const currentId = router.query.id || loadState('campId');
        if (currentId !== campaignData.id) {
          fetchCampaignData();
        }
      } else {
        resetCampaignData();
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.accessToken, router.pathname, router.query.id, router.isReady]);

  // store the campaign id in session storage whenever campaign routes are accessed
  useEffect(() => {
    if (campaignData.id && campaignData.status !== CONSTANTS.STOPPED_STATUS) {
      saveState('campId', campaignData.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaignData.id]);

  useEffect(() => {
    if (!router.pathname.includes('campaign/') && prevRoute.current) {
      prevRoute.current = false;
      removeState();
      handleCampaignData({});
    }
  }, [router.pathname]);

  function resetCampaignData() {
    removeState();
    handleCampaignData({});
    router.push(URL.SELECT_CAMPAIGN_TYPE);
  }

  // DC API Methods — stable references via latestRef
  const saveDcBasicInfoApi = useCallback(async () => {
    const { campaignData: data, accessToken, countryId: cId, deviceId: dId } = latestRef.current;
    if (!data || Object.keys(data).length === 0) {
      throw new Error('No campaign data available');
    }

    const { name, id: campaignId } = data;
    const payload = { name: name?.trim(), gameType: CONSTANTS.DC_GAME_TYPE };

    let res;
    if (!campaignId) {
      res = await saveCampaignBasicInfo(accessToken, { ...payload, countryId: cId, deviceId: dId });
      if (!res?.status) return res;
      const newId = res?.data?.id;
      if (newId && !data.id) {
        handleCampaignData(prev => ({ ...prev, id: newId }));
        saveState('campId', newId);
      }
    } else {
      res = await updateCampaignBasicInfo(accessToken, campaignId, payload);
    }
    return res;
  }, []);

  const saveDcOfferConfigAPI = useCallback(async () => {
    const { campaignData: data, accessToken } = latestRef.current;
    if (!data.id) throw new Error('No campaign id');

    const { id: campaignId, offerStartDate, offerEndDate, offerConfig, config } = data;
    const {
      heading,
      description,
      offerValue,
      offerType,
      minSpend,
      startTime,
      endTime,
      validDays,
      terms,
    } = offerConfig;

    const payload = {
      heading,
      offerValue,
      offerType: offerType || 'F',
      description,
      minSpend,
      startDate: formatDateToYYYYMMDD(offerStartDate),
      endDate: formatDateToYYYYMMDD(offerEndDate),
      startTime,
      endTime,
      terms,
      validDays: validDays || [],
      bgColor: config?.bgColor,
      textColor: config?.textColor,
      textFont: config?.textFont,
    };
    return saveOfferConfig(accessToken, campaignId, payload);
  }, []);

  const saveDcTargetConfigAPI = useCallback(async () => {
    const { campaignData: data, accessToken } = latestRef.current;
    if (!data.id) throw new Error('No campaign id');

    const { startDate, endDate, config } = data;
    const {
      minSpend,
      maxSpend,
      startTime,
      endTime,
      validDays,
      visits,
      weeks,
      bgColor,
      textColor,
      textFont,
    } = config;

    const payload = {
      startDate: formatDateToYYYYMMDD(startDate),
      endDate: formatDateToYYYYMMDD(endDate),
      startTime,
      endTime,
      minSpend,
      maxSpend,
      visits,
      weeks,
      validDays: validDays || [],
      bgColor,
      textColor,
      textFont,
    };
    return saveTargetUserConfig(accessToken, data.id, payload);
  }, []);

  const saveDcCampaignAPI = useCallback(async () => {
    const { campaignData: data, accessToken } = latestRef.current;
    if (!data.id) throw new Error('No campaign id');

    const { dailyCouponLimit, totalCouponsLimit, enablePrinting, addToWallet } = data.config || {};
    const payload = {
      dailyCouponLimit: dailyCouponLimit || 0,
      totalCouponsLimit: totalCouponsLimit || 0,
      enablePrinting: enablePrinting || false,
      addToWallet: addToWallet || false,
    };
    return launchDC(accessToken, data.id, payload);
  }, []);

  // NOM API Methods — stable references via latestRef
  // Each method receives form data from the step component (via useImperativeHandle)
  // and calls the existing API functions. Business logic is unchanged.

  const saveNomBasicInfoApi = useCallback(async formData => {
    const { accessToken, countryId: cId, deviceId: dId, campaignData: data } = latestRef.current;
    const { name, startDate, endDate, expiryDate } = formData;

    let res;
    if (!data.id) {
      res = await postCampaign(accessToken, {
        name,
        startDate,
        endDate,
        expiryDate,
        gameType: CONSTANTS.NOM_GAME_TYPE,
        fkDeviceId: dId,
        fkCountryId: cId,
      });
      if (!res?.status) return res;
      const newId = res?.data?.id;
      if (newId) {
        const camp = await getCampaign(accessToken, newId);
        if (camp.status) {
          if (camp.data.status !== CONSTANTS.STOPPED_STATUS) saveState('campId', newId);
          handleCampaignData(camp.data);
        }
      }
    } else {
      res = await updateCampaign(accessToken, { name, startDate, endDate, expiryDate }, data.id);
      if (res?.status) {
        handleCampaignData(prev => ({
          ...prev,
          ...res.data,
          endDate,
          offerEndDate: expiryDate,
        }));
      }
    }
    return res;
  }, []);

  const saveNomDesignApi = useCallback(async designData => {
    const { accessToken } = latestRef.current;
    const res = await saveDesignCampaignData(accessToken, designData, CONSTANTS.NOM_GAME_TYPE);
    if (res?.status) {
      handleCampaignData(prev => ({ ...prev, ...res.data }));
    }
    return res;
  }, []);

  const saveNomSetupApi = useCallback(async setupData => {
    const { accessToken } = latestRef.current;
    const res = await saveCampaignSetupData(accessToken, setupData, CONSTANTS.NOM_GAME_TYPE);
    if (res?.status) {
      handleCampaignData(prev => ({ ...prev, ...res.data }));
    }
    return res;
  }, []);

  const saveNomReviewApi = useCallback(async reviewData => {
    const { accessToken } = latestRef.current;
    const res = await saveCampaignReviewData(accessToken, reviewData, CONSTANTS.NOM_GAME_TYPE);
    if (res?.status) {
      handleCampaignData(prev => ({ ...prev, ...res.data }));
    }
    return res;
  }, []);

  // WIN API Methods — stable references via latestRef
  // Same pattern as NOM methods. Each receives form data from the step
  // component (via useImperativeHandle) and calls existing API functions.
  // Business logic is unchanged.

  const saveWinBasicInfoApi = useCallback(async formData => {
    const { accessToken, countryId: cId, deviceId: dId, campaignData: data } = latestRef.current;
    const { name, startDate, endDate, expiryDate } = formData;

    let res;
    if (!data.id) {
      res = await postCampaign(accessToken, {
        name,
        startDate,
        endDate,
        expiryDate,
        gameType: CONSTANTS.WIN_GAME_TYPE,
        fkDeviceId: dId,
        fkCountryId: cId,
      });
      if (!res?.status) return res;
      const newId = res?.data?.id;
      if (newId) {
        const camp = await getCampaign(accessToken, newId);
        if (camp.status) {
          if (camp.data.status !== CONSTANTS.STOPPED_STATUS) saveState('campId', newId);
          handleCampaignData(camp.data);
        }
      }
    } else {
      res = await updateCampaign(accessToken, { name, startDate, endDate, expiryDate }, data.id);
      if (res?.status) {
        handleCampaignData(prev => ({
          ...prev,
          ...res.data,
          endDate,
          offerEndDate: expiryDate,
        }));
      }
    }
    return res;
  }, []);

  const saveWinDesignApi = useCallback(async designData => {
    const { accessToken } = latestRef.current;
    const res = await saveDesignCampaignData(accessToken, designData, CONSTANTS.WIN_GAME_TYPE);
    if (res?.status) {
      handleCampaignData(prev => ({ ...prev, ...res.data }));
    }
    return res;
  }, []);

  const saveWinSetupApi = useCallback(async setupData => {
    const { accessToken } = latestRef.current;
    const res = await saveCampaignSetupData(accessToken, setupData, CONSTANTS.WIN_GAME_TYPE);
    if (res?.status) {
      handleCampaignData(prev => ({ ...prev, ...res.data }));
    }
    return res;
  }, []);

  const saveWinReviewApi = useCallback(async reviewData => {
    const { accessToken } = latestRef.current;
    const res = await saveCampaignReviewData(accessToken, reviewData, CONSTANTS.WIN_GAME_TYPE);
    if (res?.status) {
      handleCampaignData(prev => ({ ...prev, ...res.data }));
    }
    return res;
  }, []);

  const sharedState = useMemo(
    () => ({
      campaignData,
      handleCampaignData,
      preview,
      setPreview,
      saveDcBasicInfoApi,
      saveDcOfferConfigAPI,
      saveDcTargetConfigAPI,
      saveDcCampaignAPI,
      saveNomBasicInfoApi,
      saveNomDesignApi,
      saveNomSetupApi,
      saveNomReviewApi,
      saveWinBasicInfoApi,
      saveWinDesignApi,
      saveWinSetupApi,
      saveWinReviewApi,
    }),
    [
      campaignData,
      preview,
      saveDcBasicInfoApi,
      saveDcOfferConfigAPI,
      saveDcTargetConfigAPI,
      saveDcCampaignAPI,
      saveNomBasicInfoApi,
      saveNomDesignApi,
      saveNomSetupApi,
      saveNomReviewApi,
      saveWinBasicInfoApi,
      saveWinDesignApi,
      saveWinSetupApi,
      saveWinReviewApi,
    ],
  );

  return <CampaignContext.Provider value={sharedState}>{children}</CampaignContext.Provider>;
}

export function useCampaignContext() {
  return useContext(CampaignContext);
}

CampaignWrapper.propTypes = {
  children: PropTypes.any,
};
